import React, { useCallback } from 'react';

import { mdiClose } from '@lumapps/lumx/icons';
import { Chip, Icon, SelectMultiple, SelectMultipleProps, Size } from '@lumapps/lumx/react';
import { useTranslate, getKeyForLanguage } from '@lumapps/translations';

import { useLanguageSelector } from '../../hooks/useLanguageSelector';
import { LanguageSelectorProps } from '../LanguageSelector/LanguageSelector';
import { LanguageSelectorList } from '../LanguageSelector/LanguageSelectorList';

export interface LanguageSelectorMultipleProps extends Omit<LanguageSelectorProps, 'selected' | 'onChange'> {
    /** array of selected language id */
    selected?: string[];
    /** callback to be executed once the languages have changed */
    onChange: (language: string[]) => void;
}

/**
 * Select element to choose multiple languages.
 *
 * @family Translations
 * @param LanguageSelectorMultipleProps
 * @returns LanguageSelectorMultiple
 */
const LanguageSelectorMultiple = ({
    autofocus = false,
    className,
    isSearchable = false,
    keepLanguageOrder = false,
    languages,
    onChange,
    open = false,
    selected = [],
    selectProps = {},
    separatorPosition,
    textFieldProps = {},
    translateLanguages,
    isReadOnly = false,
}: LanguageSelectorMultipleProps) => {
    const { translateKey } = useTranslate();
    /** Callback to be executed once a language is selected */
    const handleSelectLanguage = useCallback(
        (language: string) => {
            let newLanguages;

            if (selected.includes(language)) {
                newLanguages = selected.filter((lang) => lang !== language);
            } else {
                newLanguages = [...selected, language];
            }
            onChange(newLanguages);
        },
        [onChange, selected],
    );

    const {
        inputRef,
        getShouldDisplaySeparator,
        filteredLanguages,
        onLanguageSelected,
        onDropdownClose,
        onClick,
        isOpen,
        filterValue,
        setFilterValue,
    } = useLanguageSelector({
        defaultOpen: open,
        translateLanguages,
        languages,
        onChange: handleSelectLanguage,
        keepLanguageOrder,
        isSearchable,
        separatorPosition,
        autofocus,
    });

    /** The selector value should be:
     *
     * - empty if no language was selected
     * - translated via the default translation function
     * - in rare cases, translated via the custom translation function passed as prop
     */
    const langValue: string[] = React.useMemo(() => {
        if (!selected || selected.length === 0) {
            return [];
        }
        if (!translateLanguages) {
            return selected.map((value) => translateKey(getKeyForLanguage(value)));
        }
        return translateLanguages(selected).map((lang) => lang.label);
    }, [selected, translateLanguages, translateKey]);

    const isItemSelected = (langKey: string) => selected.includes(langKey);

    const selectedLanguageChipRender: SelectMultipleProps['selectedChipRender'] = React.useCallback(
        (lang: string, index: number) => {
            return (
                <Chip
                    isClickable
                    key={index}
                    after={<Icon icon={mdiClose} size={Size.xxs} />}
                    size={Size.s}
                    onAfterClick={() => handleSelectLanguage(selected[index])}
                    onClick={() => handleSelectLanguage(selected[index])}
                >
                    {lang}
                </Chip>
            );
        },
        [handleSelectLanguage, selected],
    );

    return (
        <SelectMultiple
            className={className}
            value={langValue}
            isOpen={isReadOnly ? false : isOpen}
            onInputClick={onClick}
            onDropdownClose={onDropdownClose}
            selectedChipRender={selectedLanguageChipRender}
            {...selectProps}
        >
            <LanguageSelectorList
                isSearchable={isSearchable}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
                searchInputRef={inputRef}
                textFieldProps={textFieldProps}
                languages={filteredLanguages}
                getShouldDisplayAsSelected={isItemSelected}
                getShouldDisplaySeparator={getShouldDisplaySeparator}
                onLanguageSelected={onLanguageSelected}
            />
        </SelectMultiple>
    );
};

export { LanguageSelectorMultiple };
